import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { MdsScoreOption } from 'src/services/documentService/mdsWorksheet';
import type { MdsAuditEntry } from './use-mds-worksheet-audit';
import { actorLabel, scoreValueLabel, shortDate, submissionSourceLabel } from './worksheet-utils';

/**
 * BRK-1861: full field-audit drawer — the complete change history for one
 * worksheet item (who / when / version / value / source), newest first. Reads
 * the append-only submission trail assembled by useMdsWorksheetAudit; nothing
 * here writes.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  itemCode: string | null;
  itemLabel: string | null;
  entries: MdsAuditEntry[];
  options: MdsScoreOption[];
  loading: boolean;
  error: string | null;
};

const WorksheetAuditDrawer: React.FC<Props> = ({
  open,
  onClose,
  itemCode,
  itemLabel,
  entries,
  options,
  loading,
  error,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: 420, maxWidth: '100vw', p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ mb: 1 }}
        >
          <Stack sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{itemCode} — change history</Typography>
            {itemLabel && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {itemLabel}
              </Typography>
            )}
          </Stack>
          <IconButton
            size="small"
            aria-label="Close audit history"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 1 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : entries.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 2 }}
          >
            No changes recorded for this item yet.
          </Typography>
        ) : (
          <Stack
            divider={<Divider flexItem />}
            gap={1}
          >
            {entries.map((entry) => (
              <Stack
                key={entry.versionNumber}
                gap={0.25}
                sx={{ py: 1 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ flexGrow: 1 }}
                  >
                    {scoreValueLabel(entry.itemValue, options)}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`v${entry.versionNumber}`}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {actorLabel(entry.submittedByName, entry.submittedByRole)}
                  {entry.submittedAt ? ` · ${shortDate(entry.submittedAt.slice(0, 10))}` : ''}
                  {' · '}
                  {submissionSourceLabel(
                    entry.sourceType,
                    entry.sourceDocumentType,
                    entry.sourceDateOfService
                  )}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default WorksheetAuditDrawer;
